import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';
import AppHeader from '../AppHeader';
import { useLanguage } from '../LanguageContext';
import { el as elM, en as enM } from '../i18n/modules';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const WarnIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const ProjectCharter = ({ project, user, token, onBack, canEdit = true, onLogout, onProfileEdit }: {
  project: any;
  user: any;
  token: string;
  onBack: () => void; onLogout?: () => void; onProfileEdit?: () => void;
  canEdit?: boolean;
}) => {
  const { language } = useLanguage();
  const M = language === 'en' ? enM : elM;
  const L = M.projectCharter;
  const C = M.common;
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [showAIConfirm, setShowAIConfirm] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);

  useEffect(() => { fetchModuleData(); }, []);

  const fetchModuleData = async () => {
    try {
      const response = await axios.get(
        `${API}/modules/${project.id}/Project%20Charter`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data) {
        setData(response.data.data);
        setFormData(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching module data:', err);
    } finally {
      setLoading(false);
    }
  };

  const doGenerateWithAI = async () => {
    setShowAIConfirm(false);
    setAiGenerating(true);
    try {
      const res = await axios.post(
        `${API}/modules/${project.id}/charter/generate`,
        { userId: user.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.data) {
        setData(res.data.data);
        setFormData(res.data.data);
      }
    } catch (err) {
      console.error('Error generating charter:', err);
      alert(L.errorGenerate);
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/modules/${project.id}/Project%20Charter`,
        { data: formData, userId: user.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(formData);
      setEditing(false);
    } catch (err) {
      console.error('Error saving:', err);
    } finally {
      setSaving(false);
    }
  };

  const addListItem = (field: string) => {
    setFormData({ ...formData, [field]: [...(formData[field] || []), ''] });
  };

  const updateListItem = (field: string, index: number, value: string) => {
    const updated = [...(formData[field] || [])];
    updated[index] = value;
    setFormData({ ...formData, [field]: updated });
  };

  const removeListItem = (field: string, index: number) => {
    const updated = [...(formData[field] || [])];
    updated.splice(index, 1);
    setFormData({ ...formData, [field]: updated });
  };

  const exportToWord = async () => {
    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({ text: 'PROJECT CHARTER', heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER, spacing: { after: 200 } }),
          new Paragraph({ text: project.name, heading: HeadingLevel.HEADING_2, alignment: AlignmentType.CENTER, spacing: { after: 400 } }),
          new Paragraph({ text: L.sectionScope, heading: HeadingLevel.HEADING_2, spacing: { before: 300, after: 100 } }),
          new Paragraph({ text: data?.scope || '—', spacing: { after: 300 } }),
          new Paragraph({ text: L.sectionObjectives, heading: HeadingLevel.HEADING_2, spacing: { before: 300, after: 100 } }),
          ...(data?.objectives || []).map((obj: string) => new Paragraph({ text: `• ${obj}`, spacing: { after: 80 } })),
          new Paragraph({ text: L.sectionDeliverables, heading: HeadingLevel.HEADING_2, spacing: { before: 300, after: 100 } }),
          ...(data?.deliverables || []).map((d: string) => new Paragraph({ text: `• ${d}`, spacing: { after: 80 } })),
          new Paragraph({ text: L.sectionStakeholders, heading: HeadingLevel.HEADING_2, spacing: { before: 300, after: 100 } }),
          ...(data?.stakeholders || []).map((s: string) => new Paragraph({ text: `• ${s}`, spacing: { after: 80 } })),
          new Paragraph({ text: L.sectionBudget, heading: HeadingLevel.HEADING_2, spacing: { before: 300, after: 100 } }),
          new Paragraph({ text: data?.budget || '—', spacing: { after: 200 } }),
          new Paragraph({ text: L.sectionDuration, heading: HeadingLevel.HEADING_2, spacing: { before: 300, after: 100 } }),
          new Paragraph({ text: data?.duration || '—', spacing: { after: 200 } }),
          new Paragraph({
            children: [new TextRun({ text: C.wordFooter(user.full_name, new Date().toLocaleDateString(C.dateLocale)), italics: true, size: 18, color: '666666' })],
            alignment: AlignmentType.RIGHT,
            spacing: { before: 400 },
            border: { top: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' } },
          }),
        ],
      }],
    });
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `Project_Charter_${project.name}.docx`);
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <AppHeader user={user} onHome={onBack} onProfileEdit={onProfileEdit} onLogout={onLogout} />

      {/* Breadcrumb */}
      <div style={styles.breadcrumb}>
        <span style={styles.breadcrumbLink} onClick={onBack}>{C.myProjects}</span>
        <span style={styles.breadcrumbSep}> / </span>
        <span style={styles.breadcrumbLink} onClick={onBack}>{project.name}</span>
        <span style={styles.breadcrumbSep}> / </span>
        <span style={styles.breadcrumbCurrent}>Project Charter</span>
      </div>

      <div style={styles.main}>
        <div style={styles.titleRow}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={styles.pageTitle}>Project Charter</h2>
            {/* Read-only badge */}
            {!canEdit && (
              <span style={{ backgroundColor: '#f3f4f6', color: '#9ca3af', fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '999px', border: '1px solid #e5e7eb' }}>
                {C.readOnly}
              </span>
            )}
          </div>
          <div style={styles.titleButtons}>
            {!editing && data && (
              <button style={styles.exportWordBtn} onClick={exportToWord}>{C.exportWord}</button>
            )}
            {/* Εμφανίζεται μόνο αν canEdit */}
            {canEdit && (
              editing ? (
                <>
                  <button style={styles.cancelBtn} onClick={() => { setEditing(false); setFormData(data); }}>{C.cancel}</button>
                  <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>{saving ? C.saving : C.save}</button>
                </>
              ) : (
                <>
                  {data && <button style={styles.editBtn} onClick={() => setEditing(true)}>{C.edit}</button>}
                  <button
                    style={{ backgroundColor: aiGenerating ? '#7c3aed' : '#6d28d9', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: aiGenerating ? 'not-allowed' : 'pointer', opacity: aiGenerating ? 0.85 : 1 }}
                    onClick={() => setShowAIConfirm(true)}
                    disabled={aiGenerating}
                  >
                    {aiGenerating ? C.generating : data ? C.regenerateAI : C.createAI}
                  </button>
                </>
              )
            )}
          </div>
        </div>

        {loading ? (
          <p style={styles.loading}>{C.loading}</p>
        ) : aiGenerating ? (
          <div style={{ backgroundColor: '#ede9fe', border: '1px solid #c4b5fd', borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>✦</div>
            <p style={{ fontSize: '16px', fontWeight: '600', color: '#4f46e5', margin: '0 0 6px' }}>{L.generatingTitle}</p>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>{L.analyzingContract}</p>
          </div>
        ) : !data ? (
          <div style={styles.emptyState}>
            <p>{L.emptyState}</p>
          </div>
        ) : (
          <div style={styles.content}>
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>{L.sectionScope}</h3>
              {editing && canEdit ? (
                <textarea style={styles.textarea} value={formData.scope || ''} onChange={(e) => setFormData({ ...formData, scope: e.target.value })} />
              ) : (
                <p style={styles.text}>{data.scope || '—'}</p>
              )}
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>{L.sectionObjectives}</h3>
              {editing && canEdit ? (
                <div>
                  {(formData.objectives || []).map((obj: string, i: number) => (
                    <div key={i} style={styles.listItemEdit}>
                      <input style={styles.listInput} value={obj} onChange={(e) => updateListItem('objectives', i, e.target.value)} />
                      <button style={styles.removeBtn} onClick={() => removeListItem('objectives', i)}>✕</button>
                    </div>
                  ))}
                  <button style={styles.addBtn} onClick={() => addListItem('objectives')}>{C.addItem}</button>
                </div>
              ) : (
                <ul style={styles.list}>
                  {(data.objectives || []).map((obj: string, i: number) => (<li key={i} style={styles.listItem}>{obj}</li>))}
                </ul>
              )}
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>{L.sectionDeliverables}</h3>
              {editing && canEdit ? (
                <div>
                  {(formData.deliverables || []).map((d: string, i: number) => (
                    <div key={i} style={styles.listItemEdit}>
                      <input style={styles.listInput} value={d} onChange={(e) => updateListItem('deliverables', i, e.target.value)} />
                      <button style={styles.removeBtn} onClick={() => removeListItem('deliverables', i)}>✕</button>
                    </div>
                  ))}
                  <button style={styles.addBtn} onClick={() => addListItem('deliverables')}>{C.addItem}</button>
                </div>
              ) : (
                <ul style={styles.list}>
                  {(data.deliverables || []).map((d: string, i: number) => (<li key={i} style={styles.listItem}>{d}</li>))}
                </ul>
              )}
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>{L.sectionStakeholders}</h3>
              {editing && canEdit ? (
                <div>
                  {(formData.stakeholders || []).map((s: string, i: number) => (
                    <div key={i} style={styles.listItemEdit}>
                      <input style={styles.listInput} value={s} onChange={(e) => updateListItem('stakeholders', i, e.target.value)} />
                      <button style={styles.removeBtn} onClick={() => removeListItem('stakeholders', i)}>✕</button>
                    </div>
                  ))}
                  <button style={styles.addBtn} onClick={() => addListItem('stakeholders')}>{C.addItem}</button>
                </div>
              ) : (
                <ul style={styles.list}>
                  {(data.stakeholders || []).map((s: string, i: number) => (<li key={i} style={styles.listItem}>{s}</li>))}
                </ul>
              )}
            </div>

            <div style={styles.twoCol}>
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>{L.sectionBudget}</h3>
                {editing && canEdit ? (
                  <input style={styles.input} value={formData.budget || ''} onChange={(e) => setFormData({ ...formData, budget: e.target.value })} />
                ) : (
                  <p style={styles.text}>{data.budget || '—'}</p>
                )}
              </div>
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>{L.sectionDuration}</h3>
                {editing && canEdit ? (
                  <input style={styles.input} value={formData.duration || ''} onChange={(e) => setFormData({ ...formData, duration: e.target.value })} />
                ) : (
                  <p style={styles.text}>{data.duration || '—'}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {showAIConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '28px 32px', width: '420px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7c3aed', flexShrink: 0, fontSize: '20px' }}>✦</div>
              <div>
                <p style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 4px' }}>{L.aiConfirmTitle}</p>
                <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>{C.aiConfirmSubtitle}</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', backgroundColor: '#fffbeb', borderRadius: '8px', padding: '10px 12px', marginBottom: '20px', border: '1px solid #fde68a', color: '#92400e' }}>
              <WarnIcon />
              <p style={{ fontSize: '13px', margin: 0, lineHeight: 1.5, color: '#92400e' }}>{L.aiConfirmWarning}</p>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowAIConfirm(false)} style={{ backgroundColor: 'transparent', border: '1px solid #ddd', color: '#374151', padding: '8px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>{C.back}</button>
              <button onClick={doGenerateWithAI} style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>{C.continue}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  header: { backgroundColor: '#1a1a2e', padding: '0 32px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { color: 'white', fontSize: '22px', fontWeight: 'bold', margin: 0 },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  userName: { color: 'white', fontSize: '14px' },
  userRole: { backgroundColor: '#4f46e5', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
  breadcrumb: { padding: '16px 32px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', fontSize: '14px', position: 'sticky' as const, top: 64, zIndex: 99 },
  breadcrumbLink: { color: '#4f46e5', cursor: 'pointer', fontWeight: '500' },
  breadcrumbSep: { color: '#9ca3af', margin: '0 8px' },
  breadcrumbCurrent: { color: '#374151', fontWeight: '500' },
  main: { padding: '32px 48px', width: '100%', boxSizing: 'border-box' as const },
  titleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' },
  pageTitle: { fontSize: '24px', fontWeight: '600', color: '#1a1a2e', margin: 0 },
  titleButtons: { display: 'flex', gap: '12px', alignItems: 'center' },
  editBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  saveBtn: { backgroundColor: '#10b981', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '8px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  exportWordBtn: { backgroundColor: '#2b579a', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  loading: { color: '#666' },
  emptyState: { textAlign: 'center', padding: '60px', backgroundColor: 'white', borderRadius: '12px', color: '#666' },
  content: { display: 'flex', flexDirection: 'column', gap: '20px' },
  section: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  sectionTitle: { fontSize: '15px', fontWeight: '600', color: '#4f46e5', margin: '0 0 12px 0' },
  text: { fontSize: '14px', color: '#374151', margin: 0, lineHeight: '1.6' },
  textarea: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', minHeight: '80px', resize: 'vertical' as const, fontFamily: 'inherit', boxSizing: 'border-box' as const },
  input: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' as const },
  list: { margin: 0, paddingLeft: '20px' },
  listItem: { fontSize: '14px', color: '#374151', marginBottom: '6px' },
  listItemEdit: { display: 'flex', gap: '8px', marginBottom: '8px' },
  listInput: { flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', fontFamily: 'inherit' },
  removeBtn: { backgroundColor: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px' },
  addBtn: { backgroundColor: 'transparent', border: '1px dashed #4f46e5', color: '#4f46e5', padding: '6px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', marginTop: '4px' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' },
};

export default ProjectCharter;
